/**
 * 利用規約・プライバシーポリシーの改定履歴 — SSOT
 *
 * 使うところ:
 * - 各ページ下の「改定履歴」と、過去の版のページ（/privacy/versions/<施行日>）
 * - 改定したときにログイン中の利用者へ出す「主な変更点」（同意画面・改定の告知）
 *
 * 改定するとき（手順の全体は docs/features/policy-revisions.md）:
 * 1. 本文を書き換える前に、今の版を残す: `node scripts/archive-policy.mjs <privacy|terms> <今の版の施行日>`
 * 2. 本文と「最終更新日」を直し、ここの先頭に 1 件足す（changes は平易な文で 1〜5 行。「変更前 → 変更後」が分かるように）
 * 3. 利用者に知らせる改定なら notice を付け、backend/template.yaml の TermsPolicyVersion / PrivacyPolicyVersion を
 *    その施行日にする（policyRevisions.test.ts が一致を確かめる）。版を上げる反映は、画面を公開したあとにする
 *
 * notice:
 * - 'agreement'      … 同意し直すまで使えない（利用者に不利な規約の変更など）。同意画面で、チェックを外した状態で求める
 * - 'acknowledgement' … 止めずに告知し、「内容を確認しました」で閉じる。確認するまで AI アシスタントだけ使えない
 * - なし              … 告知しない（表記の修正など）。改定履歴には載せる
 */

export const POLICY_DOC = {
  TERMS: 'terms',
  PRIVACY: 'privacy',
} as const;

export type PolicyDoc = (typeof POLICY_DOC)[keyof typeof POLICY_DOC];

export const POLICY_NOTICE = {
  AGREEMENT: 'agreement',
  ACKNOWLEDGEMENT: 'acknowledgement',
} as const;

export type PolicyNotice = (typeof POLICY_NOTICE)[keyof typeof POLICY_NOTICE];

export interface PolicyRevision {
  /** 施行日（YYYY-MM-DD）。過去の版の URL と、同意の版（template.yaml）にも使う */
  date: string;
  /** 主な変更点 */
  changes: string[];
  /** 利用者への影響（告知で一緒に出す） */
  impact?: string;
  notice?: PolicyNotice;
}

export const POLICY_LABEL: Record<PolicyDoc, string> = {
  [POLICY_DOC.TERMS]: '利用規約',
  [POLICY_DOC.PRIVACY]: 'プライバシーポリシー',
};

export const POLICY_PATH: Record<PolicyDoc, string> = {
  [POLICY_DOC.TERMS]: '/terms',
  [POLICY_DOC.PRIVACY]: '/privacy',
};

/** 文書ごとの改定（新しい順） */
export const POLICY_REVISIONS: Record<PolicyDoc, PolicyRevision[]> = {
  [POLICY_DOC.PRIVACY]: [
    {
      date: '2026-10-20',
      changes: [
        'AIアシスタント（AIチャット）を追加し、発言と回答を会話として保存するようにしました（30日後に自動で削除します）',
        'AIチャットの利用状況（使った機能・回数・使った量など。発言の本文は含みません）を記録するようにしました',
        'AIの処理を日本国内のリージョン（東京・大阪）で行い、入力した内容がAIモデルの学習に使われないことを明記しました',
      ],
      impact: 'AIアシスタントを使わなければ、新しく保存される情報はありません。',
      notice: POLICY_NOTICE.ACKNOWLEDGEMENT,
    },
    {
      date: '2026-09-26',
      changes: ['取得する情報に、プロフィール画像（アイコン）を追加しました'],
    },
    {
      date: '2026-07-03',
      changes: [
        'LINE連携・LINEミニアプリを利用する場合に取得する情報（LINEユーザーID・表示名など）を追加しました',
        'LINEヤフー株式会社による情報の取得・利用と、同社グループ（日本国外を含む）への提供について追記しました',
      ],
    },
    {
      date: '2026-06-21',
      changes: [
        '委託先の Amazon Bedrock の用途を「AI企画提案の推論処理」と明記しました（このときは最終更新日の表記を変えていませんでした）',
      ],
    },
    {
      date: '2026-03-08',
      changes: [
        '有料プランの提供開始にあわせて、決済関連情報（契約状況・請求履歴）とその利用目的を追加しました',
        '決済の委託先として Stripe, Inc.（米国）を追加し、外国にある第三者への提供について記載しました',
        '個人情報取扱事業者の氏名、開示等の請求の範囲、漏えい等が発生した場合の対応（第11条）を明記しました',
      ],
    },
    {
      date: '2025-12-16',
      changes: ['全面的に改定しました'],
      notice: POLICY_NOTICE.AGREEMENT,
    },
  ],
  [POLICY_DOC.TERMS]: [
    {
      date: '2026-09-26',
      changes: [
        '利用者が投稿する情報の例にアイコン画像を加え、禁止事項に画像のアップロードを含めることを明記しました',
      ],
    },
    {
      date: '2026-07-03',
      changes: [
        'LINEミニアプリ・LINE連携を利用する場合に、LINEヤフー株式会社の規約等も適用されることを追記しました',
        'LINE等の外部サービスの都合で、利用・ログイン・通知が制限・停止されることがある旨を追記しました',
      ],
    },
    {
      date: '2026-03-10',
      changes: [
        '支払方法に Apple Pay・Google Pay を追加しました（このときは最終更新日の表記を変えていませんでした）',
      ],
    },
    {
      date: '2026-03-08',
      changes: [
        '有料プラン（ベーシック・プレミアム）の提供開始にあわせて、料金・支払い・自動更新・解約・返金の条件を追加しました（第4条）',
        '当方の都合でサービスを提供できなかった場合の返金または期間の延長を追記しました',
        '規約を変更する場合の条件と、重要な変更は原則14日前に知らせることを明記しました',
        '専属的合意管轄裁判所を東京地方裁判所とし、運営者の氏名を記載しました',
      ],
    },
    {
      date: '2025-12-16',
      changes: ['全面的に改定しました'],
      notice: POLICY_NOTICE.AGREEMENT,
    },
  ],
};

/** 今の版（同意・確認を取り直す版）= 告知する改定のうち最新の施行日 */
export function currentNoticeVersion(doc: PolicyDoc): string | undefined {
  return POLICY_REVISIONS[doc].find((revision) => revision.notice)?.date;
}

/** 「2026年10月20日」 */
export function formatPolicyDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return `${year}年${month}月${day}日`;
}
